import { differenceInDays } from 'date-fns';

export const dateView = (date: string) => {
	const _date = new Date(date);

	const time_confirm = new Date().getTime() - _date.getTime();

	// 작성한 지 24시간이 지났을 때
	if (time_confirm >= 86400000) {
		const date_d = differenceInDays(new Date(), _date);

		if (date_d < 31) return `${date_d}일 전`;
		else if (date_d >= 31 && date_d < 365) return `${Math.floor(date_d / 30)}달 전`;
		else if (date_d > 365) return `${Math.floor(date_d / 365)}년 전`;
	} else {
		// 작성한 지 1분 내일 때
		if (time_confirm < 60000) {
			return '방금 전';
			// 작성한 지 1시간 내일 때
		} else if (time_confirm >= 60000 && time_confirm < 3600000) {
			const time = time_confirm / (1000 * 60);

			return `${Math.floor(time)}분 전`;
		} else if (time_confirm >= 3600000 && time_confirm < 86400000) {
			const time = time_confirm / (1000 * 60 * 60);

			return `${Math.floor(time)}시간 전`;
		}
	}
};

export interface DataItem {
	date: string;
	value: number;
}

export interface GroupedData {
	week: string;
	dates: string[];
	data: number;
}

export const groupOptionList = ['1차 미션', '2차 미션', '3차 미션'] as const;

export const rankingGroupOptionList = [
	'전체 미션',
	'1차 미션',
	'2차 미션',
	'3차 미션',
] as const;

export type TGroupOption = (typeof groupOptionList)[number];

export type TRankingGroupOption = (typeof rankingGroupOptionList)[number];

export const groupOptionMapper: Record<TGroupOption, 'W' | 'M' | 'D'> = {
	'1차 미션': 'W',
	'2차 미션': 'M',
	'3차 미션': 'D',
};

export const rankingGroupOptionMapper: Record<
	TRankingGroupOption,
	'A' | 'W' | 'M' | 'D'
> = {
	'전체 미션': 'A',
	'1차 미션': 'W',
	'2차 미션': 'M',
	'3차 미션': 'D',
};

export function groupDataByOption(
	option: 'W' | 'M' | 'D',
	data_option: 'average' | 'last',
	dates: string[],
	values: number[],
): GroupedData[] {
	const data: DataItem[] = dates.map((date, index) => ({
		date: date,
		value: values[index],
	}));

	data.forEach((item) => {
		item.date = new Date(item.date).toDateString();
	});

	const groupedData: Record<string, { dates: string[]; total: number; datas: number[] }> =
		{};

	if (option === 'W') {
		// 주별 그룹화
		data.forEach((item) => {
			const weekNumber = getWeekNumber(new Date(item.date));
			const key = `${weekNumber}`;
			if (!groupedData[key]) {
				groupedData[key] = { dates: [], total: 0, datas: [] };
			}
			groupedData[key].dates.push(item.date);
			groupedData[key].total += item.value;
			groupedData[key].datas.push(item.value);
		});
	} else if (option === 'M') {
		// 월별 그룹화
		data.forEach((item) => {
			const year = new Date(item.date).getFullYear();
			const month = new Date(item.date).getMonth() + 1; // 월은 0부터 시작하므로 1을 더함
			const key = `${year}-${month}`;
			if (!groupedData[key]) {
				groupedData[key] = { dates: [], total: 0, datas: [] };
			}
			groupedData[key].dates.push(item.date);
			groupedData[key].total += item.value;
			groupedData[key].datas.push(item.value);
		});
	} else if (option === 'D') {
		// 일별 그룹화
		data.forEach((item) => {
			const year = new Date(item.date).getFullYear();
			const month = new Date(item.date).getMonth() + 1; // 월은 0부터 시작하므로 1을 더함
			const day = new Date(item.date).getDate();
			const key = `${year}-${month}-${day}`;
			if (!groupedData[key]) {
				groupedData[key] = { dates: [], total: 0, datas: [] };
			}
			groupedData[key].dates.push(item.date);
			groupedData[key].total += item.value;
			groupedData[key].datas.push(item.value);
		});
	}

	// 결과 데이터 생성
	const result: GroupedData[] = [];
	for (const key in groupedData) {
		const group = groupedData[key];
		const average = (group.total / group.dates.length)?.toFixed(0);
		const last = group.datas[group.datas.length - 1];

		// console.log(group);

		result.push({
			week: key,
			dates: group.dates,
			data: data_option === 'average' ? Number(average) : Number(last),
		});
	}

	return result;
}

function getWeekNumber(date: Date) {
	const year = date.getFullYear();
	const month = date.getMonth();
	const day = date.getDate();

	// 이번 달의 마지막 일요일을 찾는다.
	const lastSunday = new Date(year, month + 1, 0);
	while (lastSunday.getDay() !== 0) {
		lastSunday.setDate(lastSunday.getDate() - 1);
	}

	// 다음 달의 첫 토요일을 찾는다.
	const nextSaturday = new Date(year, month + 1, 1);
	while (nextSaturday.getDay() !== 6) {
		nextSaturday.setDate(nextSaturday.getDate() + 1);
	}

	// 저번 달의 마지막 일요일을 찾는다.
	const lastMonthLastSunday = new Date(year, month, 0);
	while (lastMonthLastSunday.getDay() !== 0) {
		lastMonthLastSunday.setDate(lastMonthLastSunday.getDate() - 1);
	}

	// 이번 달 첫번째 일
	const firstDay = new Date(year, month, 1);

	const weekNumber = Math.ceil((day + firstDay.getDay()) / 7);

	// 이번 달의 마지막 주와 다음 달의 첫 주가 겹칠 경우
	const isLastWeek =
		day >= lastSunday.getDate() && month !== nextSaturday.getMonth() && weekNumber > 4;

	if (isLastWeek) {
		if (month === 11) return `${String(year + 1)?.slice(-2)}년 1월 1주차`;
		return `${String(year)?.slice(-2)}년 ${month + 2}월 1주차`;
	} else return `${String(year)?.slice(-2)}년 ${month + 1}월 ${weekNumber}주차`;
}
